import React, { useEffect, useState } from 'react';
import { Gamepad2, Users, TrendingUp, Star } from 'lucide-react';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatCard = ({ icon: Icon, title, value, change, color }) => (
  <div className="gradient-card border border-gray-800 rounded-xl p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-sm mb-1">{title}</p>
        <h3 className="text-3xl font-bold text-white">{value}</h3>
        {change && (
          <p className={`text-sm mt-2 ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change > 0 ? '+' : ''}{change}% from last month
          </p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="text-white" size={24} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalGames: 0,
    totalUsers: 0,
    activeGames: 0,
    totalPlays: 0
  });
  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data
      const [statsRes, chartRes, activityRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/analytics/weekly-activity'),
        api.get('/analytics/recent-activity'),
      ]);

      setStats(statsRes.data.data);
      setChartData(chartRes.data.data);
      setRecentActivity(activityRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-400">Overview of your FunPlus platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Gamepad2}
          title="Total Games"
          value={stats.totalGames}
          change={stats.gamesGrowth || 12}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          icon={Users}
          title="Total Users"
          value={stats.totalUsers}
          change={stats.usersGrowth || 8}
          color="bg-gradient-to-br from-cyan-500 to-cyan-600"
        />
        <StatCard
          icon={TrendingUp}
          title="Active Games"
          value={stats.activeGames}
          change={stats.activeGamesGrowth || 5}
          color="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          icon={Star}
          title="Total Plays"
          value={stats.totalPlays.toLocaleString()}
          change={stats.playsGrowth || 15}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Chart */}
      <div className="gradient-card border border-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold mb-6">Weekly Activity</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="name" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip
              contentStyle={{ 
                backgroundColor: '#1A1A2E', 
                border: '1px solid #374151', 
                borderRadius: '8px' 
              }}
              labelStyle={{ color: '#fff' }}
            />
            <Bar dataKey="plays" fill="url(#colorGradient)" radius={[8, 8, 0, 0]} />
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6C63FF" />
                <stop offset="100%" stopColor="#00D9FF" />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Activity */}
      <div className="gradient-card border border-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold mb-6">Recent Activity</h2>
        <div className="space-y-4">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center gap-4 p-4 bg-dark rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl">{activity.icon}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium">{activity.title}</p>
                  <p className="text-sm text-gray-400">{activity.description}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time}</span>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-400 py-8">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;